import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";

import ExcelJS from "exceljs";

import {
	Box,
	Typography,
	Paper,
	TextField,
	Button,
	Chip,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	InputAdornment,
	Tooltip,
	IconButton,
	Checkbox,
	MenuItem,
} from "@mui/material";

import Alert from "@mui/material/Alert";

import {
	useTheme,
	alpha,
} from "@mui/material/styles";

import SearchIcon from "@mui/icons-material/Search";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { tokens } from "../../theme";

import {
	getCandidaturesQuery,
	softDeleteCandidature,
	updateCandidatureChecked,
} from "../../utils/firebase/firebase.datatable";

import FunSurpriseModal from "./fun-surprise-modal";


const DAILY_SURPRISE_KEY =
	"pitaya_fun_surprise_daily_v1";

const SURPRISE_DAILY_CHANCE =
	0.5;


// ============================================================
// HELPERS
// ============================================================

function getTodayKey() {
	const now =
		new Date();

	const year =
		now.getFullYear();

	const month =
		String(
			now.getMonth() + 1
		).padStart(
			2,
			"0"
		);

	const day =
		String(
			now.getDate()
		).padStart(
			2,
			"0"
		);

	return `${year}-${month}-${day}`;
}


function getFormData(item) {
	return item?.formData || {};
}


function timestampToDate(value) {
	if (!value) {
		return null;
	}

	if (
		typeof value?.toDate ===
		"function"
	) {
		return value.toDate();
	}

	if (value?.seconds) {
		return new Date(
			value.seconds * 1000
		);
	}

	const parsed =
		new Date(value);

	return Number.isNaN(
		parsed.getTime()
	)
		? null
		: parsed;
}


function getCandidatureReferenceDate(
	item
) {
	return (
		item?.completedAt ||
		item?.lastInteractionAt ||
		item?.updatedAt ||
		item?.lastInteractionAtClient ||
		item?.startedAtClient ||
		null
	);
}


function formatDate(value) {
	const date =
		timestampToDate(value);

	if (!date) {
		return "-";
	}

	return new Intl.DateTimeFormat(
		"pt-BR",
		{
			day:
				"2-digit",

			month:
				"2-digit",

			year:
				"numeric",

			hour:
				"2-digit",

			minute:
				"2-digit",
		}
	).format(date);
}


function normalizeText(value) {
	return String(
		value ?? ""
	)
		.normalize("NFD")
		.replace(
			/[\u0300-\u036f]/g,
			""
		)
		.toLowerCase();
}


function safeValue(value) {
	if (
		value === null ||
		value === undefined ||
		value === ""
	) {
		return "-";
	}

	if (
		typeof value ===
		"boolean"
	) {
		return value
			? "Sim"
			: "Não";
	}

	if (
		Array.isArray(value)
	) {
		return value.join(", ");
	}

	if (
		typeof value ===
		"object"
	) {
		return JSON.stringify(
			value
		);
	}

	return String(value);
}


const OPTION_LABELS = {
	clt:
		"CLT",

	autonomo:
		"Autônomo",

	autonoma:
		"Autônomo",

	desempregado:
		"Desempregado",

	desempregada:
		"Desempregado",

	empresario:
		"Empresário",

	negocio_proprio:
		"Negócio próprio",

	mei:
		"MEI",

	aposentada:
		"Aposentado",

	estudante:
		"Estudante",

	sim:
		"Sim",

	nao:
		"Não",

	online:
		"Online",

	presencial:
		"Presencial",

	imediatamente:
		"Imediatamente",

	alguns_dias:
		"Alguns dias",

	ainda_avaliando:
		"Ainda avaliando",

	redes_sociais:
		"Redes sociais",

	confianca_marca:
		"Confiança na marca",

	substituir_marca:
		"Substituir marca atual",

	agregar_pitaya:
		"Agregar Pitaya",

	primeira_experiencia:
		"Primeira experiência",

	ja_revendeu:
		"Já revendeu",

	mae:
		"Mãe",

	pai:
		"Pai",

	filho:
		"Filho(a)",

	irma:
		"Irmã",

	irmao:
		"Irmão",

	amigo:
		"Amigo(a)",

	amiga:
		"Amigo(a)",

	solteira:
		"Solteira",

	solteiro:
		"Solteiro",

	casada:
		"Casada",

	casado:
		"Casado",

	divorciada:
		"Divorciada",

	divorciado:
		"Divorciado",

	viuva:
		"Viúva",

	viuvo:
		"Viúvo",

	superior_incompleto:
		"Superior incompleto",

	superior_completo:
		"Superior completo",

	medio_completo:
		"Ensino médio completo",

	medio_incompleto:
		"Ensino médio incompleto",

	mais_5_anos:
		"Mais de 5 anos",
};


function formatOption(value) {
	if (
		value === null ||
		value === undefined ||
		value === ""
	) {
		return "-";
	}

	return (
		OPTION_LABELS[value] ||
		String(value)
			.replaceAll(
				"_",
				" "
			)
	);
}


// ============================================================
// EXCEL
// ============================================================

async function exportCandidaturesToExcel(
	items
) {
	const workbook =
		new ExcelJS.Workbook();

	workbook.creator =
		"Pitaya Joias";

	workbook.created =
		new Date();

	const worksheet =
		workbook.addWorksheet(
			"Candidaturas"
		);

	worksheet.views = [
		{
			state:
				"frozen",

			ySplit:
				1,
		},
	];


	worksheet.columns = [
		{
			header:
				"Data Candidatura",

			key:
				"completedAt",

			width:
				22,
		},
		{
			header:
				"Nome",

			key:
				"nome",

			width:
				30,
		},
		{
			header:
				"E-mail",

			key:
				"email",

			width:
				34,
		},
		{
			header:
				"Whatsapp",

			key:
				"whatsapp",

			width:
				20,
		},
		{
			header:
				"Data Nascimento",

			key:
				"dataNascimento",

			width:
				20,
		},
		{
			header:
				"CPF",

			key:
				"cpf",

			width:
				18,
		},
		{
			header:
				"RG",

			key:
				"rg",

			width:
				18,
		},
		{
			header:
				"Estado civil",

			key:
				"estadoCivil",

			width:
				20,
		},
		{
			header:
				"Endereço",

			key:
				"endereco",

			width:
				32,
		},
		{
			header:
				"Número",

			key:
				"numero",

			width:
				12,
		},
		{
			header:
				"Complemento",

			key:
				"complemento",

			width:
				22,
		},
		{
			header:
				"Bairro",

			key:
				"bairro",

			width:
				24,
		},
		{
			header:
				"CEP",

			key:
				"cep",

			width:
				16,
		},
		{
			header:
				"Cidade",

			key:
				"cidade",

			width:
				26,
		},
	];


	items.forEach(
		(item) => {
			const form =
				getFormData(
					item
				);

			worksheet.addRow({
				completedAt:
					formatDate(
						getCandidatureReferenceDate(
							item
						)
					),

				nome:
					safeValue(
						form.nome
					),

				email:
					safeValue(
						form.email
					),

				whatsapp:
					safeValue(
						form.whatsapp
					),

				dataNascimento:
					safeValue(
						form.dataNascimento
					),

				cpf:
					safeValue(
						form.cpf
					),

				rg:
					safeValue(
						form.rg
					),

				estadoCivil:
					formatOption(
						form.estadoCivil
					),

				endereco:
					safeValue(
						form.endereco
					),

				numero:
					safeValue(
						form.numero
					),

				complemento:
					safeValue(
						form.complemento
					),

				bairro:
					safeValue(
						form.bairro
					),

				cep:
					safeValue(
						form.cep
					),

				cidade:
					safeValue(
						form.cidade
					),
			});
		}
	);


	const headerRow =
		worksheet.getRow(1);

	headerRow.height =
		28;

	headerRow.eachCell(
		(cell) => {
			cell.font = {
				bold:
					true,

				color: {
					argb:
						"FFFFFFFF",
				},
			};

			cell.fill = {
				type:
					"pattern",

				pattern:
					"solid",

				fgColor: {
					argb:
						"FF1F4E3D",
				},
			};

			cell.alignment = {
				vertical:
					"middle",

				horizontal:
					"left",
			};
		}
	);


	worksheet.autoFilter = {
		from: {
			row:
				1,

			column:
				1,
		},

		to: {
			row:
				1,

			column:
				worksheet
					.columns
					.length,
		},
	};


	const buffer =
		await workbook.xlsx.writeBuffer();

	const blob =
		new Blob(
			[
				buffer,
			],
			{
				type:
					"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			}
		);

	const url =
		URL.createObjectURL(
			blob
		);

	const link =
		document.createElement(
			"a"
		);

	const today =
		new Date()
			.toLocaleDateString(
				"pt-BR"
			)
			.replaceAll(
				"/",
				"-"
			);

	link.href =
		url;

	link.download =
		`candidaturas-revendedoras-${today}.xlsx`;

	document.body.appendChild(
		link
	);

	link.click();

	document.body.removeChild(
		link
	);

	URL.revokeObjectURL(
		url
	);
}


// ============================================================
// COMPONENT
// ============================================================

const CandidaturePage = () => {
	const theme =
		useTheme();

	const colors =
		tokens(
			theme.palette.mode
		);


	const [
		candidatures,
		setCandidatures,
	] = useState([]);


	const [
		loading,
		setLoading,
	] = useState(true);


	const [
		deletingId,
		setDeletingId,
	] = useState(null);


	const [
		exporting,
		setExporting,
	] = useState(false);


	const [
		search,
		setSearch,
	] = useState("");


	const [
		startDate,
		setStartDate,
	] = useState(null);


	const [
		endDate,
		setEndDate,
	] = useState(null);


	const [
		selectedUf,
		setSelectedUf,
	] = useState("");


	const [
		checkedFilter,
		setCheckedFilter,
	] = useState("");


	const [
		checkingId,
		setCheckingId,
	] = useState(null);


	const [
		error,
		setError,
	] = useState("");


	const [
		open,
		setOpen,
	] = useState(false);


	// ========================================================
	// SURPRISE
	// ========================================================

	useEffect(() => {
		const today =
			getTodayKey();

		try {
			const stored =
				localStorage.getItem(
					DAILY_SURPRISE_KEY
				);

			if (stored) {
				const parsed =
					JSON.parse(
						stored
					);

				if (
					parsed?.date ===
					today
				) {
					return;
				}
			}


			const shouldShow =
				Math.random() <
				SURPRISE_DAILY_CHANCE;


			localStorage.setItem(
				DAILY_SURPRISE_KEY,
				JSON.stringify({
					date:
						today,

					shouldShow,

					checkedAt:
						new Date()
							.toISOString(),
				})
			);


			if (
				shouldShow
			) {
				setOpen(true);
			}
		} catch (error) {
			console.error(
				"Erro ao controlar FunSurpriseModal:",
				error
			);

			setOpen(false);
		}
	}, []);


	// ========================================================
	// THEME
	// ========================================================

	const pageBg =
		theme.palette.mode ===
			"dark"
			? colors.pink[500]
			: theme.palette
				.background
				.default;


	const cardBg =
		theme.palette.mode ===
			"dark"
			? colors.pink[800]
			: "#ffffff";


	const subtleBorder =
		theme.palette.mode ===
			"dark"
			? `1px solid ${colors.grey[700]}`
			: "1px solid rgba(0,0,0,0.08)";


	const softText =
		theme.palette.mode ===
			"dark"
			? colors.grey[200]
			: "text.secondary";


	// ========================================================
	// LOAD
	// ========================================================

	const loadCandidatures =
		useCallback(
			async () => {
				try {
					setLoading(true);

					setError("");

					const data =
						await getCandidaturesQuery();

					setCandidatures(
						data
					);
				} catch (err) {
					console.error(
						err
					);

					setError(
						"Não foi possível carregar as candidaturas."
					);
				} finally {
					setLoading(false);
				}
			},
			[]
		);


	useEffect(() => {
		loadCandidatures();
	}, [
		loadCandidatures,
	]);


	// ========================================================
	// UFs DISPONÍVEIS
	// ========================================================

	const availableUfs =
		useMemo(() => {
			return Array.from(
				new Set(
					candidatures
						.map(
							(item) =>
								String(
									getFormData(
										item
									)
										.uf ||
										""
								)
									.trim()
									.toUpperCase()
						)
						.filter(
							Boolean
						)
				)
			).sort();
		}, [
			candidatures,
		]);


	// ========================================================
	// FILTER
	// ========================================================

	const filteredCandidatures =
		useMemo(
			() => {
				const term =
					normalizeText(
						search.trim()
					);


				const startBoundary =
					startDate
						? new Date(
							`${startDate}T00:00:00`
						)
						: null;


				const endBoundary =
					endDate
						? new Date(
							`${endDate}T23:59:59.999`
						)
						: null;


				return candidatures.filter(
					(item) => {
						const form =
							getFormData(
								item
							);


						// ========================================
						// DATA
						// ========================================

						if (
							startBoundary ||
							endBoundary
						) {
							const candidatureDate =
								timestampToDate(
									getCandidatureReferenceDate(
										item
									)
								);

							if (
								!candidatureDate
							) {
								return false;
							}


							if (
								startBoundary &&
								candidatureDate <
									startBoundary
							) {
								return false;
							}


							if (
								endBoundary &&
								candidatureDate >
									endBoundary
							) {
								return false;
							}
						}


						// ========================================
						// UF
						// ========================================

						if (
							selectedUf &&
							String(
								form.uf ||
									""
							)
								.trim()
								.toUpperCase() !==
								selectedUf
						) {
							return false;
						}


						// ========================================
						// CONFERIDO
						// ========================================

						if (
							checkedFilter ===
								"checked" &&
							item.conferido !==
								true
						) {
							return false;
						}


						if (
							checkedFilter ===
								"unchecked" &&
							item.conferido ===
								true
						) {
							return false;
						}


						// ========================================
						// BUSCA
						// ========================================

						if (!term) {
							return true;
						}


						const values = [
							form.nome,
							form.email,
							form.whatsapp,
							form.cpf,
							form.rg,
							form.cidade,
							form.uf,
							form.bairro,
							form.endereco,
							form.situacaoProfissional,
							form.vendeSemijoias,
							form.nomeIndicacao,
							form.referencia1Nome,
							form.referencia1Telefone,
							form.referencia2Nome,
							form.referencia2Telefone,
							item.currentStepName,
							item.candidatureId,
							item.source
								?.utmSource,
							item.source
								?.utmContent,
						];


						return values.some(
							(value) =>
								normalizeText(
									value
								).includes(
									term
								)
						);
					}
				);
			},
			[
				candidatures,
				search,
				startDate,
				endDate,
				selectedUf,
				checkedFilter,
			]
		);


	const hasManagementFilter =
		Boolean(
			startDate ||
				endDate ||
				selectedUf ||
				checkedFilter
		);


	function handleClearManagementFilters() {
		setStartDate(null);
		setEndDate(null);
		setSelectedUf("");
		setCheckedFilter("");
	}


	// ========================================================
	// CONFERIDO
	// ========================================================

	const handleToggleChecked =
		async (item) => {
			const previousValue =
				item.conferido;

			const nextValue =
				item.conferido !==
				true;


			try {
				setCheckingId(
					item.id
				);

				setError("");


				setCandidatures(
					(current) =>
						current.map(
							(candidate) =>
								candidate.id ===
								item.id
									? {
										...candidate,

										conferido:
											nextValue,
									}
									: candidate
						)
				);


				await updateCandidatureChecked(
					item.id,
					nextValue
				);
			} catch (err) {
				console.error(
					"Erro ao atualizar conferido:",
					err
				);


				setCandidatures(
					(current) =>
						current.map(
							(candidate) =>
								candidate.id ===
								item.id
									? {
										...candidate,

										conferido:
											previousValue,
									}
									: candidate
						)
				);


				setError(
					"Não foi possível atualizar o status de conferência."
				);
			} finally {
				setCheckingId(
					null
				);
			}
		};


	// ========================================================
	// DELETE
	// ========================================================

	const handleDelete =
		async (item) => {
			const form =
				getFormData(
					item
				);


			const confirmed =
				window.confirm(
					`Deseja remover a candidatura de ${
						form.nome ||
						"esta pessoa"
					} da listagem?`
				);


			if (
				!confirmed
			) {
				return;
			}


			try {
				setDeletingId(
					item.id
				);

				setError("");


				await softDeleteCandidature(
					item.id
				);


				setCandidatures(
					(current) =>
						current.filter(
							(candidate) =>
								candidate.id !==
								item.id
						)
				);
			} catch (err) {
				console.error(
					err
				);

				setError(
					"Não foi possível remover a candidatura."
				);
			} finally {
				setDeletingId(
					null
				);
			}
		};


	// ========================================================
	// EXPORT
	// ========================================================

	const handleExport =
		async () => {
			if (
				filteredCandidatures.length ===
				0
			) {
				window.alert(
					"Não existem candidaturas para exportar."
				);

				return;
			}


			try {
				setExporting(true);


				await exportCandidaturesToExcel(
					filteredCandidatures
				);
			} catch (err) {
				console.error(
					"Erro ao exportar Excel:",
					err
				);

				setError(
					"Não foi possível gerar o arquivo Excel."
				);
			} finally {
				setExporting(false);
			}
		};


	return (
		<>
			<FunSurpriseModal
				open={open}
				onClose={() =>
					setOpen(false)
				}
			/>


			<Box
				sx={{
					minHeight:
						"100vh",

					px: 2,

					py: 3,

					bgcolor:
						pageBg,

					display:
						"flex",

					justifyContent:
						"center",

					paddingTop:
						"80px",
				}}
			>
				<Box
					sx={{
						width:
							"100%",
					}}
				>
					<Paper
						elevation={1}
						sx={{
							p: 2.5,

							borderRadius:
								3,

							bgcolor:
								cardBg,

							border:
								subtleBorder,
						}}
					>
						{/* ===================================== */}
						{/* HEADER */}
						{/* ===================================== */}

						<Stack
							spacing={0.75}
							sx={{
								mb: 3,
							}}
							alignItems="center"
						>
							<Stack
								direction="row"
								spacing={1}
								alignItems="center"
							>
								<AssignmentIndOutlinedIcon />

								<Typography
									variant="h4"
									fontWeight={900}
								>
									Candidaturas
								</Typography>
							</Stack>


							<Typography
								variant="body2"
								color={
									softText
								}
								textAlign="center"
							>
								Gerencie as candidaturas recebidas pelo formulário de novas revendedoras.
							</Typography>
						</Stack>


						{/* ===================================== */}
						{/* RESUMO + FILTROS */}
						{/* ===================================== */}

						<Stack
							direction={{
								xs:
									"column",

								lg:
									"row",
							}}
							spacing={2}
							alignItems={{
								xs:
									"stretch",

								lg:
									"center",
							}}
							justifyContent="space-between"
							sx={{
								mb: 2.5,
							}}
						>
							<Stack
								direction={{
									xs:
										"column",

									sm:
										"row",
								}}
								spacing={1.5}
							>
								<Paper
									variant="outlined"
									sx={{
										px: 2,

										py: 1.5,

										minWidth:
											170,

										borderRadius:
											2,

										border:
											subtleBorder,

										bgcolor:
											theme
												.palette
												.mode ===
											"dark"
												? colors
													.pink[700]
												: "#fafafa",
									}}
								>
									<Typography
										variant="caption"
										color={
											softText
										}
									>
										Candidaturas
									</Typography>


									<Typography
										variant="h5"
										fontWeight={900}
										sx={{
											mt:
												0.25,
										}}
									>
										{
											candidatures.length
										}
									</Typography>
								</Paper>


								<Paper
									variant="outlined"
									sx={{
										px: 2,

										py: 1.5,

										minWidth:
											170,

										borderRadius:
											2,

										border:
											subtleBorder,

										bgcolor:
											theme
												.palette
												.mode ===
											"dark"
												? colors
													.pink[700]
												: "#fafafa",
									}}
								>
									<Typography
										variant="caption"
										color={
											softText
										}
									>
										Exibindo
									</Typography>


									<Typography
										variant="h5"
										fontWeight={900}
										sx={{
											mt:
												0.25,
										}}
									>
										{
											filteredCandidatures.length
										}
									</Typography>
								</Paper>
							</Stack>


							<Paper
								variant="outlined"
								sx={{
									p: 1.25,

									borderRadius:
										2,

									border:
										subtleBorder,

									bgcolor:
										theme
											.palette
											.mode ===
										"dark"
											? colors
												.pink[700]
											: "#fafafa",
								}}
							>
								<Stack
									direction={{
										xs:
											"column",

										sm:
											"row",
									}}
									spacing={1}
									alignItems={{
										xs:
											"stretch",

										sm:
											"center",
									}}
								>
									<Typography
										variant="caption"
										fontWeight={900}
										color={
											softText
										}
										sx={{
											px:
												0.5,

											whiteSpace:
												"nowrap",
										}}
									>
										Período
									</Typography>


									<TextField
										size="small"
										type="date"
										label="Início"
										value={
											startDate ||
											""
										}
										onChange={(
											event
										) =>
											setStartDate(
												event
													.target
													.value ||
													null
											)
										}
										InputLabelProps={{
											shrink:
												true,
										}}
										inputProps={{
											max:
												endDate ||
												undefined,
										}}
										sx={{
											width: {
												xs:
													"100%",

												sm:
													155,
											},
										}}
									/>


									<TextField
										size="small"
										type="date"
										label="Fim"
										value={
											endDate ||
											""
										}
										onChange={(
											event
										) =>
											setEndDate(
												event
													.target
													.value ||
													null
											)
										}
										InputLabelProps={{
											shrink:
												true,
										}}
										inputProps={{
											min:
												startDate ||
												undefined,
										}}
										sx={{
											width: {
												xs:
													"100%",

												sm:
													155,
											},
										}}
									/>


									<TextField
										select
										size="small"
										label="UF"
										value={
											selectedUf
										}
										onChange={(
											event
										) =>
											setSelectedUf(
												event
													.target
													.value
											)
										}
										sx={{
											width: {
												xs:
													"100%",

												sm:
													95,
											},
										}}
									>
										<MenuItem value="">
											Todas
										</MenuItem>

										{availableUfs.map(
											(uf) => (
												<MenuItem
													key={
														uf
													}
													value={
														uf
													}
												>
													{
														uf
													}
												</MenuItem>
											)
										)}
									</TextField>


									<TextField
										select
										size="small"
										label="Conferido"
										value={
											checkedFilter
										}
										onChange={(
											event
										) =>
											setCheckedFilter(
												event
													.target
													.value
											)
										}
										sx={{
											width: {
												xs:
													"100%",

												sm:
													145,
											},
										}}
									>
										<MenuItem value="">
											Todos
										</MenuItem>

										<MenuItem value="unchecked">
											Não conferidos
										</MenuItem>

										<MenuItem value="checked">
											Conferidos
										</MenuItem>
									</TextField>


									{hasManagementFilter && (
										<Button
											variant="contained"
											size="medium"
											color="error"
											onClick={
												handleClearManagementFilters
											}
											sx={{
												textTransform:
													"none",
											}}
										>
											Limpar
										</Button>
									)}
								</Stack>
							</Paper>
						</Stack>


						{/* ===================================== */}
						{/* BUSCA + EXPORT */}
						{/* ===================================== */}

						<Stack
							direction={{
								xs:
									"column",

								md:
									"row",
							}}
							spacing={1.5}
							alignItems={{
								xs:
									"stretch",

								md:
									"center",
							}}
							sx={{
								mb: 2.5,
							}}
						>
							<TextField
								fullWidth
								size="small"
								label="Buscar candidatura"
								value={
									search
								}
								onChange={(
									event
								) =>
									setSearch(
										event
											.target
											.value
									)
								}
								placeholder="Nome, telefone, CPF, cidade, e-mail..."
								InputProps={{
									startAdornment: (
										<InputAdornment
											position="start"
										>
											<SearchIcon />
										</InputAdornment>
									),
								}}
							/>


							<Button
								variant="contained"
								color="success"
								onClick={
									handleExport
								}
								disabled={
									exporting ||
									loading
								}
								startIcon={
									<FileDownloadOutlinedIcon />
								}
								sx={{
									minWidth:
										190,

									height:
										40,

									fontWeight:
										800,

									textTransform:
										"none",

									whiteSpace:
										"nowrap",
								}}
							>
								{exporting
									? "Gerando Excel..."
									: "Exportar Excel"}
							</Button>
						</Stack>


						{/* ===================================== */}
						{/* ERROR */}
						{/* ===================================== */}

						{error && (
							<Alert
								severity="error"
								sx={{
									mb: 2,
								}}
							>
								{error}
							</Alert>
						)}


						{/* ===================================== */}
						{/* LOADING */}
						{/* ===================================== */}

						{loading && (
							<Paper
								variant="outlined"
								sx={{
									p: 3,

									borderRadius:
										2,

									border:
										subtleBorder,

									bgcolor:
										theme
											.palette
											.mode ===
										"dark"
											? colors
												.pink[700]
											: "#fafafa",
								}}
							>
								<Stack
									direction="row"
									spacing={2}
									alignItems="center"
								>
									<Box
										sx={{
											width:
												42,

											height:
												42,

											borderRadius:
												"999px",

											border:
												`3px solid ${
													theme
														.palette
														.mode ===
													"dark"
														? colors
															.grey[600]
														: "rgba(0,0,0,0.18)"
												}`,

											borderTopColor:
												theme
													.palette
													.success
													.main,

											animation:
												"spin 0.9s linear infinite",
										}}
									/>


									<Stack>
										<Typography
											fontWeight={900}
										>
											Carregando candidaturas...
										</Typography>

										<Typography
											variant="body2"
											color={
												softText
											}
										>
											Aguarde enquanto buscamos os registros.
										</Typography>
									</Stack>
								</Stack>


								<Box
									sx={{
										"@keyframes spin":
											{
												"0%":
													{
														transform:
															"rotate(0deg)",
													},

												"100%":
													{
														transform:
															"rotate(360deg)",
													},
											},
									}}
								/>
							</Paper>
						)}


						{/* ===================================== */}
						{/* EMPTY */}
						{/* ===================================== */}

						{!loading &&
							filteredCandidatures.length ===
								0 && (
								<Paper
									variant="outlined"
									sx={{
										p: 4,

										textAlign:
											"center",

										borderRadius:
											2,

										border:
											subtleBorder,

										bgcolor:
											theme
												.palette
												.mode ===
											"dark"
												? colors
													.pink[700]
												: "#fafafa",
									}}
								>
									<AssignmentIndOutlinedIcon
										sx={{
											fontSize:
												42,

											opacity:
												0.5,

											mb:
												1,
										}}
									/>

									<Typography
										fontWeight={900}
									>
										Nenhuma candidatura encontrada
									</Typography>

									<Typography
										variant="body2"
										color={
											softText
										}
										sx={{
											mt:
												0.5,
										}}
									>
										Tente alterar os termos da busca.
									</Typography>
								</Paper>
							)}


						{/* ===================================== */}
						{/* TABLE */}
						{/* ===================================== */}

						{!loading &&
							filteredCandidatures.length >
								0 && (
								<TableContainer
									component={
										Paper
									}
									variant="outlined"
									sx={{
										borderRadius:
											2,

										border:
											subtleBorder,

										boxShadow:
											"none",

										maxWidth:
											"100%",

										overflowX:
											"auto",
									}}
								>
									<Table
										size="small"
										sx={{
											minWidth:
												1700,
										}}
									>
										<TableHead>
											<TableRow
												sx={{
													bgcolor:
														theme
															.palette
															.mode ===
														"dark"
															? colors
																.pink[700]
															: "#f8fafc",
												}}
											>
												<TableCell
													sx={{
														fontWeight:
															900,
													}}
												>
													Data
												</TableCell>

												<TableCell
													sx={{
														fontWeight:
															900,
													}}
												>
													Nome
												</TableCell>

												<TableCell
													sx={{
														fontWeight:
															900,
													}}
												>
													WhatsApp
												</TableCell>

												<TableCell
													sx={{
														fontWeight:
															900,
													}}
												>
													E-mail
												</TableCell>

												<TableCell
													sx={{
														fontWeight:
															900,
													}}
												>
													Cidade
												</TableCell>

												<TableCell
													sx={{
														fontWeight:
															900,
													}}
												>
													UF
												</TableCell>

												<TableCell
													sx={{
														fontWeight:
															900,
													}}
												>
													Situação profissional
												</TableCell>

												<TableCell
													sx={{
														fontWeight:
															900,
													}}
												>
													Consignado
												</TableCell>

												<TableCell
													sx={{
														fontWeight:
															900,
													}}
												>
													Semijoias
												</TableCell>

												<TableCell
													sx={{
														fontWeight:
															900,
													}}
												>
													Início
												</TableCell>

												<TableCell
													sx={{
														fontWeight:
															900,
													}}
												>
													Como conheceu
												</TableCell>

												<TableCell
													sx={{
														fontWeight:
															900,
													}}
												>
													Origem
												</TableCell>

												<TableCell
													align="center"
													sx={{
														fontWeight:
															900,
													}}
												>
													Status
												</TableCell>

												<TableCell
													sx={{
														fontWeight:
															900,
													}}
												>
													Etapa
												</TableCell>

												<TableCell
													align="center"
													sx={{
														fontWeight:
															900,
													}}
												>
													Conferido
												</TableCell>

												<TableCell
													align="center"
													sx={{
														fontWeight:
															900,
													}}
												>
													Ação
												</TableCell>
											</TableRow>
										</TableHead>


										<TableBody>
											{filteredCandidatures.map(
												(
													item
												) => {
													const form =
														getFormData(
															item
														);


													const isDeleting =
														deletingId ===
														item.id;


													const isCompleted =
														item.status ===
														"concluida";


													const origem = [
														item
															.source
															?.utmSource,

														item
															.source
															?.utmContent,
													]
														.filter(
															Boolean
														)
														.join(
															" / "
														);


													return (
														<TableRow
															key={
																item.id
															}
															hover
															sx={{
																"&:last-child td":
																	{
																		borderBottom:
																			0,
																	},
															}}
														>
															<TableCell
																sx={{
																	whiteSpace:
																		"nowrap",
																}}
															>
																<Tooltip
																	title={
																		isCompleted
																			? "Data de conclusão"
																			: "Última interação"
																	}
																>
																	<span>
																		{formatDate(
																			getCandidatureReferenceDate(
																				item
																			)
																		)}
																	</span>
																</Tooltip>
															</TableCell>


															<TableCell
																sx={{
																	fontWeight:
																		800,

																	whiteSpace:
																		"nowrap",
																}}
															>
																{safeValue(
																	form.nome
																)}
															</TableCell>


															<TableCell
																sx={{
																	whiteSpace:
																		"nowrap",
																}}
															>
																{safeValue(
																	form.whatsapp
																)}
															</TableCell>


															<TableCell>
																{safeValue(
																	form.email
																)}
															</TableCell>


															<TableCell
																sx={{
																	whiteSpace:
																		"nowrap",
																}}
															>
																{safeValue(
																	form.cidade
																)}
															</TableCell>


															<TableCell>
																<Chip
																	size="small"
																	variant="outlined"
																	label={
																		safeValue(
																			form.uf
																		)
																	}
																	sx={{
																		fontWeight:
																			800,
																	}}
																/>
															</TableCell>


															<TableCell>
																{formatOption(
																	form.situacaoProfissional
																)}
															</TableCell>


															<TableCell>
																{formatOption(
																	form.trabalhouConsignado
																)}
															</TableCell>


															<TableCell>
																{formatOption(
																	form.vendeSemijoias
																)}
															</TableCell>


															<TableCell>
																{formatOption(
																	form.previsaoInicio
																)}
															</TableCell>


															<TableCell>
																{formatOption(
																	form.comoConheceu
																)}
															</TableCell>


															<TableCell>
																{origem ||
																	"-"}
															</TableCell>


															<TableCell
																align="center"
															>
																<Chip
																	size="small"
																	label={
																		isCompleted
																			? "Concluída"
																			: item.status ===
																				"em_andamento"
																				? "Em andamento"
																				: safeValue(
																					item.status
																				)
																	}
																	sx={{
																		fontWeight:
																			800,

																		bgcolor:
																			isCompleted
																				? alpha(
																					theme
																						.palette
																						.success
																						.main,
																					0.12
																				)
																				: alpha(
																					theme
																						.palette
																						.warning
																						.main,
																					0.12
																				),

																		color:
																			isCompleted
																				? theme
																					.palette
																					.success
																					.main
																				: theme
																					.palette
																					.warning
																					.main,
																	}}
																/>
															</TableCell>


															<TableCell
																sx={{
																	whiteSpace:
																		"nowrap",
																}}
															>
																{!isCompleted ? (
																	<Typography
																		variant="body2"
																		fontWeight={800}
																	>
																		{item.currentStepName ||
																			"-"}
																	</Typography>
																) : null}
															</TableCell>


															<TableCell
																align="center"
															>
																<Tooltip
																	title={
																		item.conferido ===
																		true
																			? "Marcar como não conferido"
																			: "Marcar como conferido"
																	}
																>
																	<span>
																		<Checkbox
																			checked={
																				item.conferido ===
																				true
																			}
																			disabled={
																				checkingId ===
																				item.id
																			}
																			onChange={() =>
																				handleToggleChecked(
																					item
																				)
																			}
																			color="success"
																			size="small"
																		/>
																	</span>
																</Tooltip>
															</TableCell>


															<TableCell
																align="center"
															>
																<Tooltip
																	title="Remover candidatura"
																>
																	<span>
																		<IconButton
																			size="small"
																			disabled={
																				isDeleting
																			}
																			onClick={() =>
																				handleDelete(
																					item
																				)
																			}
																			sx={{
																				color:
																					theme
																						.palette
																						.error
																						.main,

																				border:
																					`1px solid ${alpha(
																						theme
																							.palette
																							.error
																							.main,
																						0.25
																					)}`,

																				borderRadius:
																					2,

																				"&:hover":
																					{
																						bgcolor:
																							alpha(
																								theme
																									.palette
																									.error
																									.main,
																								0.08
																							),
																					},
																			}}
																		>
																			<DeleteOutlineIcon
																				fontSize="small"
																			/>
																		</IconButton>
																	</span>
																</Tooltip>
															</TableCell>
														</TableRow>
													);
												}
											)}
										</TableBody>
									</Table>
								</TableContainer>
							)}
					</Paper>
				</Box>
			</Box>
		</>
	);
};


export default CandidaturePage;